<template>
  <div class="container">
    <div class="row">
      <div class="col-md-8 col-md-offset-2">
        <div class="bsd-section">
          <breadcrumb />
        </div>

        <div class="bsd-section">
          <alert-box ref="alertBox" class="spacer-l" />

          <template v-if="error">
            <router-link
              tag="button"
              :to="{ name: 'panel'}"
              class="z-button z-button--secondary z-button--block"
            >
              Voltar para página inicial
            </router-link>
          </template>
        </div>

        <boleto-loader v-if="loading" />

        <boleto-generator
          v-else-if="!error"
          :title="`Pagar ${periodName} com boleto`"
          :title-on-ready="`Copie o código ou baixe o boleto para pagar o ${periodName}`"
          :payment="paymentPromise"
          :ready="boletoReady"
          :waiting="boletoWaiting"
          :unavailable="boletoUnavailable"
          :callback-clipboard="copySuccess"
          :handle-download-boleto="downloadBoleto"
          :handle-generate-boleto="createContractDischargeRequest"
        >
          <contract-full-amount />
        </boleto-generator>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>

.payment-code {
  color: var(--color-neutral-black);
  font-size: rem(16);
  line-height: rem(24);
  margin-bottom: var(--space-extra-large);
}
</style>

<script>
import { mapState, mapGetters } from 'vuex';
import AlertBox from 'components/zilla/alert-box';
import BoletoLoader from 'pages/shared/boleto/boleto-loader';
import BoletoGenerator from 'pages/shared/boleto/boleto-generator';
import Breadcrumb from 'components/breadcrumb';
import ContractFullAmount from 'pages/contract-payment/contract-full-amount';
import BoletoService from 'services/BoletoService';
import ContractFullPaymentService from 'services/ContractFullPaymentService';

export default {
  components: {
    AlertBox,
    BoletoLoader,
    BoletoGenerator,
    Breadcrumb,
    ContractFullAmount,
  },
  data: () => ({
    bill: {},
    paymentPromise: {},
    error: false,
    loading: true,
    boletoUnavailable: false,
    boletoWaiting: false,
    boletoReady: false,
    hasPix: false,
  }),
  beforeMount() {
    if (!this.showContractDischarge) {
      this.$router.push({ name: 'panel' });
    }
  },
  computed: {
    ...mapState(['currentContract', 'currentBilling']),
    ...mapGetters(['showContractDischarge']),
    periodName() {
      return this.currentContract.period_name;
    },
    contractId() {
      return this.currentContract.id;
    },
  },
  methods: {
    loadExistingContractDischargeRequest() {
      ContractFullPaymentService.getExistingPaymentPromise(this.contractId)
        .then(({ request }) => {
          this.verifyAndGetExistingBoletoPaymentPromise(request);
        })
        .catch(() => {
          this.showErrorMessage();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    verifyAndGetExistingBoletoPaymentPromise(request) {
      if (request === null
        || request.status !== 'pending'
        || (!request.boleto_payment_promise && request.pix_payment_promise)
      ) {
        this.boletoUnavailable = true;
        this.paymentPromise = null;

        if (request?.pix_payment_promise) {
          this.alreadyHasPix();
        }
      } else if (!request?.boleto_payment_promise) {
        this.boletoWaiting = true;
      } else {
        this.paymentPromise = request.boleto_payment_promise;
        this.validatePaymentPromise();
      }
    },
    createContractDischargeRequest() {
      this.boletoWaiting = true;
      this.boletoUnavailable = false;

      this.setLoadingTimeout();

      if (this.hasPix) {
        this.$refs.alertBox.hide();
      }

      return ContractFullPaymentService.charge('boleto', this.contractId)
        .then(({ request }) => {
          this.paymentPromise = request.boleto_payment_promise;
          this.validatePaymentPromise();
          this.$tracker.logEvent('[contract discharge] Clicked "Gerar Boleto"', { request });
        })
        .catch(() => {
          this.showErrorMessage();
        })
        .finally(() => this.$pageLoader.hide());
    },
    validatePaymentPromise() {
      this.resetBoletoStatus();

      if (this.paymentPromise === null) {
        this.boletoUnavailable = true;
      } else if (this.paymentPromise.bar_code === null) {
        this.boletoWaiting = true;

        this.setLoadingTimeout();
      } else {
        this.boletoReady = true;
      }
    },
    downloadBoleto() {
      this.$pageLoader.show('Baixando boleto');

      const courseName = this.currentBilling.course_name;
      const institutionName = this.currentBilling.institution_name;
      const fileName = `Boleto de quitação de ${this.periodName} - ${courseName} - ${institutionName}`;

      BoletoService.downloadPromiseBoleto(this.paymentPromise.id, fileName)
        .catch((error) => {
          this.showErrorMessage();
          this.$rollbar.error(`Erro ao fazer download do boleto: ${error}`);
        })
        .finally(() => this.$pageLoader.hide());

      this.$tracker.logEvent('[contract discharge] Clicked "Baixar boleto"');
    },
    copySuccess() {
      this.$tracker.logEvent('[contract discharge] Clicked "Copiar o código do boleto"');
    },
    setLoadingTimeout() {
      setTimeout(() => {
        if (this.boletoWaiting && !this.$refs.alertBox.visible) {
          this.$refs.alertBox.show(
            'warning', 'O boleto está sendo processado',
            'Isso pode demorar alguns minutos, por favor volte mais tarde.',
          );
        }
      }, 10000);
    },
    showErrorMessage() {
      this.error = true;

      this.$refs.alertBox.show(
        'danger', 'Erro inesperado',
        'Não foi possível gerar o boleto. Tente novamente mais tarde ou entre em contato com o Quero Pago.',
      );
    },
    resetBoletoStatus() {
      this.boletoUnavailable = false;
      this.boletoWaiting = false;
      this.boletoReady = false;
    },
    alreadyHasPix() {
      this.hasPix = true;

      this.$refs.alertBox.show(
        'warning', 'PIX gerado',
        'Já foi gerado um código PIX para a quitação deste contrato. '
        + 'Se optar por realizar o pagamento via boleto, o PIX gerado será invalidado.',
        13000,
      );
    },
  },
  mounted() {
    this.loadExistingContractDischargeRequest();
    this.$tracker.logEvent('[contract discharge] Viewed Boleto Page', { contractId: this.contractId });
  },
};
</script>
